
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import StarXpGate from '@/components/star/StarXpGate.vue'
import StarTabBar from '@/components/star/StarTabBar.vue'
import StarNoData from '@/components/star/StarNoData.vue'
import StrategyCard from '@/components/star/StrategyCard.vue'
import type { StrategyItem } from '@/components/star/StrategyCard.vue'
import starZoneApi from '@/api/starZoneApi'
import { mapSquareItemToStrategy } from '@/utils/starZoneHelper'
import { useStarTheme } from '@/utils/useStarTheme'

const PAGE_SIZE = 10

export default defineComponent({
  name: 'StarSquare',
  components: { StarXpGate, StarTabBar, StarNoData, StrategyCard },
  setup() {
    const route = useRoute()
    const { colors, isDark, isLight } = useStarTheme()

    const pageStyle = computed(() => ({
      background: colors.value.bg,
      '--type-tab-color': isLight.value ? colors.value.textSecondary : 'rgba(255, 255, 255, 0.65)',
      '--type-tab-hover-color': isLight.value ? '#333333' : 'rgba(255, 255, 255, 0.85)',
      '--type-tab-active-color': colors.value.textTabActive,
    }))
    const activeTab = ref('square')
    const loading = ref(true)
    const loadingMore = ref(false)
    const activeTypeName = ref('全部策略')
    const typeTabs = ref<{ typeID: number; name: string }[]>([{ typeID: 0, name: '全部策略' }])
    const storedTypeIDList = ref<{ typeID: number; name: string }[]>([])
    const allList = ref<StrategyItem[]>([])
    const loadedTypeID = ref<number | null>(null)
    const pageNo = ref(1)
    const total = ref(0)
    const hasMore = ref(false)
    const contentRef = ref<HTMLElement | null>(null)
    const loadSentinelRef = ref<HTMLElement | null>(null)
    let loadObserver: IntersectionObserver | null = null

    const getActiveTab = () =>
      typeTabs.value.find((tab) => tab.name === activeTypeName.value)

    const getApiTypeID = () => getActiveTab()?.typeID ?? 0

    const applyTypeTabs = (typeIDList?: { typeID: number; name: string }[]) => {
      if (typeIDList?.length) {
        storedTypeIDList.value = typeIDList.slice()
      }

      const raw = storedTypeIDList.value.length
        ? storedTypeIDList.value
        : [{ typeID: 0, name: '全部策略' }]
      let tabs = raw.some((tab) => tab.name === '全部策略')
        ? raw.filter((tab) => tab.name !== '全部')
        : raw.slice()

      if (!tabs.some((tab) => tab.typeID === -1)) {
        const allIndex = tabs.findIndex((tab) => tab.name === '全部策略')
        const featuredTab = { typeID: -1, name: '精选策略' }
        if (allIndex >= 0) {
          tabs.splice(allIndex + 1, 0, featuredTab)
        } else {
          tabs = [{ typeID: 0, name: '全部策略' }, featuredTab].concat(tabs)
        }
      }

      typeTabs.value = tabs
      if (!typeTabs.value.some((tab) => tab.name === activeTypeName.value)) {
        activeTypeName.value = typeTabs.value[0]?.name ?? '全部策略'
      }
    }

    const fetchList = async (append = false) => {
      const token = route.query.token as string
      const apiTypeID = getApiTypeID()
      const reqPage = append ? pageNo.value : 1
      if (!append) loading.value = true
      else loadingMore.value = true

      try {
        const res = await starZoneApi.getStrategySquare({
          data: { pageNo: reqPage, pageSize: PAGE_SIZE, typeID: apiTypeID },
          headers: { token },
        })
        if (res?.code === 1 || res?.code === 0) {
          const data = res.data || {}
          if (!append && apiTypeID === 0) {
            applyTypeTabs(data.typeIDList)
          }
          total.value = data.total || 0
          const rows = (data.list || []).map((item: any) => mapSquareItemToStrategy(item))
          allList.value = append ? allList.value.concat(rows) : rows
          loadedTypeID.value = apiTypeID
          hasMore.value = allList.value.length < total.value
          pageNo.value = reqPage
        }
      } catch (e) {
        console.error('getStrategySquare failed', e)
      } finally {
        loading.value = false
        loadingMore.value = false
        await nextTick()
        setupLoadObserver()
      }
    }

    const switchType = (name: string) => {
      if (activeTypeName.value === name) return
      activeTypeName.value = name

      const nextApiTypeID = getApiTypeID()
      if (loadedTypeID.value !== nextApiTypeID) {
        fetchList(false)
      }
    }

    const loadMore = () => {
      if (loading.value || loadingMore.value || !hasMore.value) return
      pageNo.value += 1
      fetchList(true)
    }

    const setupLoadObserver = () => {
      loadObserver?.disconnect()
      const target = loadSentinelRef.value
      if (!target || !hasMore.value || loading.value || loadingMore.value) return

      loadObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            loadMore()
          }
        },
        {
          root: contentRef.value,
          rootMargin: '80px 0px',
          threshold: 0,
        }
      )
      loadObserver.observe(target)
    }

    watch(
      () => [allList.value.length, hasMore.value, loading.value] as const,
      () => {
        if (!loading.value) {
          nextTick(setupLoadObserver)
        }
      }
    )

    onMounted(() => fetchList(false))

    onBeforeUnmount(() => {
      loadObserver?.disconnect()
      loadObserver = null
    })

    return {
      activeTab,
      loading,
      loadingMore,
      activeTypeName,
      typeTabs,
      allList,
      hasMore,
      contentRef,
      loadSentinelRef,
      colors,
      isDark,
      isLight,
      pageStyle,
      switchType,
    }
  },
})
